import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ClockIcon } from "@heroicons/react/24/outline";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import PairingHistoryTable from "./PairingHistoryTable";
import { deviceService } from "../../api/services/deviceService";

interface ScreenDeviceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenId: string;
  screenName?: string;
}

/**
 * Read-only device history for one screen/location: which physical devices
 * have served it and when (assign / unassign / resume events), newest first.
 * Answers "which TVs ran this location, and were there gaps?".
 */
export default function ScreenDeviceHistoryModal({
  isOpen,
  onClose,
  screenId,
  screenName,
}: ScreenDeviceHistoryModalProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["screenDeviceHistory", screenId],
    queryFn: () => deviceService.getScreenDeviceHistory(screenId),
    enabled: isOpen && !!screenId,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        screenName ? `Device History — ${screenName}` : "Screen Device History"
      }
      size="full"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader size="small" />
        </div>
      ) : isError ? (
        <div className="text-center py-10 text-red-500 text-sm">
          Failed to load device history.
        </div>
      ) : !data || data.events.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          <ClockIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No device history recorded for this screen yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            History is collected from the day the feature was enabled.
          </p>
        </div>
      ) : (
        <PairingHistoryTable events={data.events} variant="screen" />
      )}
    </Modal>
  );
}
